import React, { useState, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { RefreshCw, ChevronDown } from 'lucide-react';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  pullDistance?: number;
  className?: string;
  disabled?: boolean;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  threshold = 60,
  pullDistance = 120,
  className,
  disabled = false,
}) => {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullOffset, setPullOffset] = useState(0);
  const [startY, setStartY] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled || isRefreshing) return;
    
    // Only start pulling if at the top of the scroll container
    const container = containerRef.current;
    if (container && container.scrollTop === 0) {
      setStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const offset = Math.max(0, currentY - startY);
    
    // Apply resistance to make it feel more natural
    const resistance = Math.min(offset / 2.5, pullDistance);
    setPullOffset(resistance);
    
    // Prevent default scrolling when pulling
    if (offset > 10) {
      e.preventDefault();
    }
  }, [isPulling, startY, pullDistance, disabled, isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;
    
    setIsPulling(false);
    
    if (pullOffset >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setPullOffset(0);
  }, [isPulling, pullOffset, threshold, onRefresh, disabled, isRefreshing]);

  const refreshProgress = Math.min(pullOffset / threshold, 1);
  const shouldTrigger = pullOffset >= threshold;

  return (
    <div
      ref={containerRef}
      className={cn("relative overflow-auto touch-manipulation", className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Refresh indicator */}
      <div
        className={cn(
          "absolute top-0 left-0 right-0 flex items-center justify-center transition-all duration-300 ease-out z-10",
          isPulling || isRefreshing ? "opacity-100" : "opacity-0"
        )}
        style={{
          height: Math.max(pullOffset, isRefreshing ? 60 : 0),
          transform: `translateY(${isRefreshing ? 0 : Math.max(pullOffset - 60, -60)}px)`,
        }}
      >
        <div className="flex flex-col items-center space-y-2">
          <div
            className={cn(
              "relative flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200",
              shouldTrigger || isRefreshing
                ? "border-primary bg-primary/10"
                : "border-muted-foreground bg-muted"
            )}
          >
            {isRefreshing ? (
              <RefreshCw className="w-4 h-4 text-primary animate-spin" />
            ) : shouldTrigger ? (
              <ChevronDown className="w-4 h-4 text-primary rotate-180 transition-transform duration-200" />
            ) : (
              <ChevronDown 
                className="w-4 h-4 text-muted-foreground transition-transform duration-200" 
                style={{ transform: `rotate(${refreshProgress * 180}deg)` }}
              />
            )}
          </div>
          
          <span className={cn(
            "text-xs font-medium transition-colors duration-200",
            shouldTrigger || isRefreshing ? "text-primary" : "text-muted-foreground"
          )}>
            {isRefreshing 
              ? "Refreshing..." 
              : shouldTrigger 
                ? "Release to refresh" 
                : "Pull to refresh"
            }
          </span>
        </div>
      </div>
      
      {/* Content with proper spacing when refreshing */}
      <div
        className="transition-transform duration-300 ease-out"
        style={{
          transform: `translateY(${isRefreshing ? 60 : Math.max(pullOffset, 0)}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};